import React from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { PageHero, Section } from "@/components/PageShell";
import { Reveal } from "@/components/ui-bits";

const CONTENT = {
  "certificate-methodology": {
    eyebrow: "Methodology",
    title: "Certificate Methodology",
    intro: "How a Botswana Brilliance Certificate is assembled, what each record represents and how evidence is connected.",
    sections: [
      ["What the certificate is", "The Botswana Brilliance Certificate is a living digital certificate that connects a diamond's documented Botswana-origin, physical identity, chain of custody and journey. It is the authoritative record; a downloadable PDF is a point-in-time snapshot."],
      ["What Botswana Brilliance does", "We review and connect the supporting records supplied by the diamond's owner and by independent service providers. Each stage of the six-stage journey identifies the claim, the evidence supplied, the source of that evidence, the responsible provider, the verification method, the date recorded and a record reference."],
      ["What we do not do", "Botswana Brilliance does not perform independent grading and does not own every listed diamond. Independent grading remains the responsibility of the issuing laboratory. Provider statuses such as integrated, accepted, available on request or proposed do not imply a formal partnership unless explicitly labelled Formal partner."],
      ["Review and approval", "No certificate is issued automatically. Every certificate is created only after an authorised Botswana Brilliance review and approval step, and every material update is recorded in the audit history."],
    ],
  },
  "verification-limitations": {
    eyebrow: "Transparency",
    title: "Verification Limitations",
    intro: "A clear statement of what a verification result means, and what it does not.",
    sections: [
      ["What a verified result means", "A verified result confirms that a digital record exists and matches the supplied Botswana Brilliance number or QR code, and that its connected evidence is on file. It does not, on its own, constitute an independent grading opinion."],
      ["Dependence on supplied records", "The certificate reflects the records supplied by owners and independent providers. Botswana Brilliance reviews and connects these records but is not the originator of independent laboratory, identity or imaging assessments."],
      ["Status meanings", "Statuses such as Verification pending, Record updated, Ownership transfer pending or Suspended describe the current state of the record. A result should always be read together with the connected evidence and provider statuses."],
      ["No unsupported claims", "Botswana Brilliance does not make claims that cannot be independently supported by the evidence connected to a certificate."],
    ],
  },
  "participant-standards": {
    eyebrow: "Standards",
    title: "Participant Standards",
    intro: "Expectations for licensed manufacturers, dealers, brokers, laboratories and technology providers.",
    sections: [
      ["Eligibility", "Participation is limited to licensed industry participants who complete application, email verification, company and licence verification, document review and administrator approval before access is activated."],
      ["Accurate records", "Participants are responsible for the accuracy and lawful provenance of the information and documents they submit, and retain ownership and control of their diamonds at all times."],
      ["Access and security", "Participants must use multi-factor authentication and role-based access for authorised employees, and are accountable for actions taken under their accounts."],
      ["Data visibility", "Participants control whether inventory is private, visible only to Botswana Brilliance, shared with approved buyers or publicly presented."],
    ],
  },
  "data-governance": {
    eyebrow: "Governance",
    title: "Data Governance",
    intro: "How information is separated, protected and retained across the platform.",
    sections: [
      ["Public and confidential information", "The platform maintains a strict separation between authorised public information shown on verification and confidential participant and ownership data."],
      ["Provider records", "Records from independent data, technology and laboratory providers are attributed to their source and connected only where they apply to a specific diamond."],
      ["Security", "Records and documents are encrypted, actions are time-stamped in an audit history, and material updates require administrator approval."],
      ["Retention and integrity", "Certificates are living records; changes are recorded rather than overwritten, preserving a complete history for each diamond."],
    ],
  },
  "privacy-policy": {
    eyebrow: "Privacy",
    title: "Privacy Policy",
    intro: "How personal and company information is collected, used and protected.",
    sections: [
      ["Information we collect", "We collect the company, licence, contact and documentation information required to operate diamond services, together with account and security information such as authentication factors."],
      ["How we use it", "Information is used to verify participants, connect and review records, operate the certificate service and communicate about applications and services."],
      ["Your controls", "Participants control the visibility of their inventory and may request corrections to their account information, subject to review and audit."],
      ["Security", "We apply encryption, role-based access and multi-factor authentication, and separate public information from confidential records."],
    ],
  },
  "terms-of-use": {
    eyebrow: "Terms",
    title: "Terms of Use",
    intro: "The terms governing use of the Botswana Brilliance website and platform.",
    sections: [
      ["Service description", "Botswana Brilliance provides independent origin, identity and market-access diamond services. It is not a government department, government-owned company or official national authority."],
      ["Ownership", "The lawful owner retains ownership and control of the diamond. Botswana Brilliance does not own every listed diamond and does not replace independent laboratories."],
      ["Acceptable use", "Users must not misuse verification tools, misrepresent certificates or attempt to access records they are not authorised to view."],
      ["No warranty beyond evidence", "Certificates reflect the connected evidence supplied. Independent grading opinions remain those of the issuing laboratory."],
    ],
  },
  "report-concern": {
    eyebrow: "Integrity",
    title: "Report a Certificate Concern",
    intro: "If you believe a certificate, verification result or record may be inaccurate or misused, please tell us.",
    sections: [
      ["When to report", "Report a concern if a QR code or Botswana Brilliance number does not resolve to the expected record, if a certificate appears altered, or if you suspect misuse of the Botswana Pride Mark or any connected record."],
      ["What happens next", "Concerns are reviewed by Botswana Brilliance. Where appropriate, a certificate can be marked Suspended while the matter is investigated, and the audit history preserves each action taken."],
    ],
    cta: true,
  },
};

export default function Legal() {
  const { slug } = useParams();
  const data = CONTENT[slug];

  if (!data) {
    return (
      <div>
        <PageHero eyebrow="Legal & Methodology" title="Page not found" subtitle="This document is not available." />
        <Section><Link to="/" className="btn-ghost rounded-full px-6 py-3 text-sm">Return home</Link></Section>
      </div>
    );
  }

  return (
    <div>
      <PageHero eyebrow={data.eyebrow} title={data.title} subtitle={data.intro} />
      <Section>
        <div className="max-w-3xl space-y-6">
          {data.sections.map(([h, p], i) => (
            <Reveal key={h} delay={i * 0.08}>
              <div className="glass rounded-2xl p-7">
                <h2 className="font-serif text-2xl">{h}</h2>
                <p className="mt-3 text-sm text-bb-silver/70 leading-relaxed">{p}</p>
              </div>
            </Reveal>
          ))}
          {data.cta && (
            <Reveal delay={0.3}>
              <Link to="/contact" data-testid="legal-report-cta" className="btn-gold rounded-full px-7 py-3.5 text-sm inline-block">
                Submit a concern
              </Link>
            </Reveal>
          )}
          <p className="text-xs text-bb-silver/40 pt-4">
            LRG Botswana Brilliance is a Botswana-owned, SEZA-approved diamond services company operating independently in
            collaboration with licensed participants and approved service providers.
          </p>
        </div>
      </Section>
    </div>
  );
}
